/**
 * Product-sync coverage FOLD (BLU-790).
 *
 * The store-wide coverage scan used to be a 40-page server-side loop behind a
 * single request (`coverage.handler.ts`, `MAX_PRODUCTS = 2000`). At Threadheads'
 * 3,555-product catalogue that is 32-45s of blocking work, and — because it is
 * capped below the catalogue size — it answers "which values still need codes"
 * WRONG, not merely late.
 *
 * The fix rests on one property: **coverage's payload is a mergeable monoid.**
 *   - `unmapped*` tallies are `name -> count` maps  -> SUM by name
 *   - `seen*` / `seenOptionNames` are distinct sets -> UNION
 *   - `totals.*` are counts                        -> SUM
 *
 * So the server can return ONE page at a time and the client can fold them into
 * exactly the aggregate the old loop produced.
 *
 * ⚠ The one thing that does NOT merge — and the trap this module exists to make
 * unmissable — is `mappedCoverage {mapped, total}`. It is derived as
 * `seen.size - unmapped.size`. Summing that across pages double-counts every
 * value that appears on more than one page (a colour on 40 pages would report
 * `total: 40`). It MUST be derived once, at the end, from the folded sets —
 * which is why a page carries raw `seenValues`/`unmappedValues` and never a
 * `mappedCoverage` of its own.
 *
 * This module is shared so the frontend fold and the backend's
 * fold-equals-the-old-loop test run the SAME implementation. A second copy
 * would let the two drift, and the drift would be silent.
 */
export function createCoverageAccumulator() {
    return {
        unmappedProductTypes: new Map(),
        unmappedColours: new Map(),
        unmappedSizes: new Map(),
        unmappedColourSkuCodes: new Map(),
        seenProductTypes: new Set(),
        seenColours: new Set(),
        seenSizes: new Set(),
        seenColourOptionNames: new Set(),
        seenSizeOptionNames: new Set(),
        totals: { products: 0, variants: 0, ready: 0, unmappedProducts: 0 },
        pagesFolded: 0,
        storeSkuOrg: false,
    };
}
function sumInto(target, entries) {
    for (const { name, count } of entries) {
        target.set(name, (target.get(name) ?? 0) + count);
    }
}
function sumSkuCodesInto(target, entries) {
    for (const { name, skuCodes } of entries) {
        if (!skuCodes)
            continue;
        const codes = target.get(name) ?? new Map();
        for (const [code, count] of Object.entries(skuCodes))
            codes.set(code, (codes.get(code) ?? 0) + count);
        target.set(name, codes);
    }
}
function unionInto(target, values) {
    for (const value of values)
        target.add(value);
}
/**
 * Fold one page into the accumulator. Pure w.r.t. the page; mutates `acc`.
 *
 * Folding the SAME page twice double-counts — the caller is responsible for
 * feeding each cursor exactly once (the scan hook drives a strictly serial
 * cursor walk, so a page can only repeat if the cursor is replayed).
 */
export function foldCoveragePage(acc, page) {
    sumInto(acc.unmappedProductTypes, page.unmappedValues.productTypes);
    sumInto(acc.unmappedColours, page.unmappedValues.colours);
    sumInto(acc.unmappedSizes, page.unmappedValues.sizes);
    sumSkuCodesInto(acc.unmappedColourSkuCodes, page.unmappedValues.colours);
    unionInto(acc.seenProductTypes, page.seenValues.productTypes);
    unionInto(acc.seenColours, page.seenValues.colours);
    unionInto(acc.seenSizes, page.seenValues.sizes);
    unionInto(acc.seenColourOptionNames, page.seenOptionNames.colour);
    unionInto(acc.seenSizeOptionNames, page.seenOptionNames.size);
    acc.totals.products += page.totals.products;
    acc.totals.variants += page.totals.variants;
    acc.totals.ready += page.totals.ready;
    acc.totals.unmappedProducts += page.totals.unmappedProducts;
    acc.pagesFolded += 1;
    if (page.storeSkuOrg)
        acc.storeSkuOrg = true;
    return acc;
}
/**
 * Sort a tally the same way the old server loop did (`tallyToSorted`):
 * most-used first, ties broken by name. Kept byte-identical so a folded result
 * is `toEqual`-comparable with the single-loop result, not merely equivalent.
 */
function tallyToSorted(tally) {
    return [...tally.entries()]
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
}
/**
 * Attach each colour's SKU codes (86-22). Unsorted: the client orders them
 * (JSON key order is not an order anyway). A value without SKU codes is
 * returned untouched.
 */
export function withSkuCodes(usages, skuCodes) {
    if (skuCodes.size === 0)
        return usages;
    return usages.map((usage) => {
        const codes = skuCodes.get(usage.name);
        if (!codes || codes.size === 0)
            return usage;
        return { ...usage, skuCodes: Object.fromEntries(codes) };
    });
}
/**
 * Derive the store-wide summary from folded state.
 *
 * `partial` is the caller's knowledge of whether Shopify has more pages — pass
 * the last page's `hasNextPage` (or `true` if the walk was abandoned early).
 * The accumulator cannot know this on its own.
 */
export function finalizeCoverage(acc, partial) {
    return {
        unmappedValues: {
            productTypes: tallyToSorted(acc.unmappedProductTypes),
            colours: withSkuCodes(tallyToSorted(acc.unmappedColours), acc.unmappedColourSkuCodes),
            sizes: tallyToSorted(acc.unmappedSizes),
        },
        // Derived ONCE, from the folded sets. See the module header: summing a
        // per-page {mapped,total} would double-count cross-page values.
        mappedCoverage: {
            productTypes: {
                mapped: acc.seenProductTypes.size - acc.unmappedProductTypes.size,
                total: acc.seenProductTypes.size,
            },
            colours: {
                mapped: acc.seenColours.size - acc.unmappedColours.size,
                total: acc.seenColours.size,
            },
            sizes: {
                mapped: acc.seenSizes.size - acc.unmappedSizes.size,
                total: acc.seenSizes.size,
            },
        },
        totals: { ...acc.totals },
        partial,
        seenOptionNames: {
            colour: [...acc.seenColourOptionNames],
            size: [...acc.seenSizeOptionNames],
        },
        ...(acc.storeSkuOrg ? { storeSkuOrg: true } : {}),
    };
}
